'use client'

import { usePathname } from 'next/navigation'

/**
 * Renders the AEO markdown-alternate `<link>` tag entirely client-side.
 *
 * Previously the root layout read the proxy-injected `x-craft-md-url`
 * request header via `headers()` (a Next.js Dynamic API) to build this tag
 * server-side. Any call to a Dynamic API in the shared layout opts *every*
 * route in the app into per-request dynamic rendering, which defeated the
 * `export const revalidate = 86400` on every page (see the PR that added
 * this component for the full writeup + live evidence).
 *
 * This component reads the current path client-side via `usePathname()`
 * instead, which does not affect server rendering at all. React 19 /
 * Next.js hoist `<link>`/`<meta>`/`<title>` tags rendered anywhere in the
 * tree — including from a Client Component — into the document `<head>`,
 * so the tag still ends up in the right place.
 *
 * Mirrors the proxy's `x-craft-md-url` computation in `src/proxy.ts`
 * exactly: `${pathname}.md`.
 *
 * Trade-off (disclosed, not hidden): the tag is injected after hydration
 * rather than present in the initial static/ISR HTML. Crawlers that don't
 * execute JavaScript will not see it in the raw HTML source; crawlers that
 * do (Googlebot's render pass, most modern AI/answer-engine crawlers) will.
 * Restoring sitewide static/ISR rendering (the reason this moved) is the
 * higher-priority fix — this AEO link tag is a secondary, non-essential
 * signal.
 */
export default function MdAlternateLink() {
  const pathname = usePathname()
  if (!pathname) return null
  return <link rel="alternate" type="text/markdown" href={`${pathname}.md`} />
}
